import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';

/**
 * "Would the player see something spawn/despawn here?" test used by traffic, parked cars and
 * pedestrians. Uses the real camera frustum when rendering; in headless runs (unit tests) it falls
 * back to a disc around the focus roughly the size of the on-screen area.
 */
export class ViewTester {
  private readonly frustum = new THREE.Frustum();
  private readonly mat = new THREE.Matrix4();
  private readonly sphere = new THREE.Sphere();
  private stamp = -1;
  /** Extra margin (m) added to every tested radius so things don't pop in at the screen edge. */
  margin = 6;
  /** Headless fallback radius (m). */
  headlessRadius = 45;

  constructor(
    private readonly ctx: GameContext,
    private readonly focus: () => V2,
  ) {}

  private refresh(): void {
    const frame = this.ctx.state.frame;
    if (frame === this.stamp) return;
    this.stamp = frame;
    const cam = this.ctx.camera;
    this.mat.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.mat);
  }

  isVisible(x: number, z: number, radius: number): boolean {
    if (!this.ctx.render) {
      const f = this.focus();
      return Math.hypot(x - f.x, z - f.z) < this.headlessRadius + radius;
    }
    this.refresh();
    this.sphere.center.set(x, 1, z);
    this.sphere.radius = radius + this.margin;
    return this.frustum.intersectsSphere(this.sphere);
  }
}
